import { useEffect, useRef, useState } from "react";
import {
  listCanvases,
  createCanvas,
  deleteCanvas,
  seedCanvas,
  getArtifacts,
  generateReportVariant,
  listVersions,
  type CanvasSummary,
} from "./api";
import type {
  ArtifactBundle,
  Complexity,
  DeploymentStage,
  VariantKind,
  VersionSummary,
} from "./schema";
import { signOut } from "../auth";
import { useToast } from "../ui/Toast";
import { useConfirm } from "../ui/ConfirmDialog";
import { TierBadge } from "../users/TierBadge";
import { useIsFreeTier, useMe } from "../users/useMe";
import { createPortal, createSetupCheckout, confirmCard } from "../users/me-api";
import { trackEvent } from "../lib/analytics";
import { PaidFeatureBadge } from "../users/PaidFeatureBadge";
import { UpgradeModal } from "../users/UpgradeModal";

const VARIANT_LABELS: Record<VariantKind, string> = {
  "gap-summary": "Gap summary",
  scorecard: "Deployment scorecard",
  consistency: "Consistency report",
  "one-pager": "Executive one-pager",
};
const VARIANT_KINDS: VariantKind[] = [
  "gap-summary",
  "scorecard",
  "consistency",
  "one-pager",
];

interface Props {
  onOpen: (id: string) => void;
  onOpenWithUpload?: (id: string, workflowName: string) => void;
  onReview: (id: string) => void;
  onOpenVersion?: (p: {
    id: string;
    workflowName: string;
    version: number;
    createdAt: number;
    grade: string;
    composite: number;
  }) => void;
  onStartReview?: (id: string) => void;
  onOpenCoach?: (id: string, workflowName: string, version: number) => void;
}

export function CanvasHome({
  onOpen,
  onOpenWithUpload,
  onReview,
  onOpenVersion,
  onStartReview,
  onOpenCoach,
}: Props) {
  const toast = useToast();
  const confirm = useConfirm();
  const isFree = useIsFreeTier();
  const { me, refresh: refreshMe } = useMe();
  const freeAtCap = isFree && (me?.sessionsCreated ?? 0) >= 1;
  const [upgradePrompt, setUpgradePrompt] = useState<string | null>(null);
  const [sessions, setSessions] = useState<CanvasSummary[] | null>(null);
  const [workflowName, setWorkflowName] = useState("");
  const [namedOwner, setNamedOwner] = useState("");
  const [industry, setIndustry] = useState("");
  const [deploymentStage, setDeploymentStage] = useState<DeploymentStage | "">("");
  const [complexity, setComplexity] = useState<Complexity | "">("");
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [managing, setManaging] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cardRequired, setCardRequired] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);

  // Seed-demo is a paid-only convenience, and only until a seeded canvas
  // exists. Free tier has no seeded demo (a free user gets exactly one canvas,
  // created by them). Paid users can seed once; the button reappears if they
  // delete the seed. Driven by whether a seeded canvas currently exists rather
  // than the lifetime counter, so it reflects current reality.
  const hasSeeded = (sessions ?? []).some((s) => s.seeded);
  const showSeedButton = sessions !== null && !isFree && !hasSeeded;

  const refresh = async () => {
    try {
      setSessions(await listCanvases());
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => { void refresh(); }, []);

  // Returning from Stripe Checkout: ?billing=success|cancel. On success the
  // webhook may lag a beat, so refresh /me to pick up the new tier.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing");
    if (!billing) return;
    if (billing === "success") {
      toast.success("Subscription activated — welcome to Pro!");
      trackEvent("purchase", { plan: "individual" });
      void refreshMe();
    }
    params.delete("billing");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
  }, [toast, refreshMe]);

  // Create the canvas straight from a restored draft (used after card setup),
  // retrying briefly to absorb webhook lag on the cardOnFile flip.
  const createFromDraft = async (d: {
    workflowName?: string; namedOwner?: string; industry?: string;
    deploymentStage?: string; complexity?: string;
  }) => {
    setBusy(true);
    setError(null);
    setCardRequired(false);
    // Confirm the card directly with Stripe so cardOnFile is set now, rather than
    // waiting on the webhook — the create then succeeds on the first attempt.
    await confirmCard().catch(() => undefined);
    await refreshMe();
    const args = {
      workflowName: (d.workflowName ?? "").trim(),
      namedOwner: (d.namedOwner ?? "").trim(),
      industry: (d.industry ?? "").trim(),
      deploymentStage: d.deploymentStage as DeploymentStage,
      complexity: d.complexity as Complexity,
    };
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const { id } = await createCanvas(args);
        trackEvent("canvas_created", { source: "post_card_setup" });
        if (onOpenWithUpload) onOpenWithUpload(id, args.workflowName);
        else onOpen(id);
        return;
      } catch (e) {
        const code = (e as { code?: string }).code;
        if (code === "CARD_REQUIRED" && attempt < 3) {
          await new Promise((r) => setTimeout(r, 1500)); // wait for the webhook
          continue;
        }
        if (code === "CARD_REQUIRED") {
          toast.success("Card saved — click Open Canvas to continue.");
        } else {
          setError((e as Error).message);
        }
        setBusy(false);
        return;
      }
    }
    setBusy(false);
  };

  // Returning from Stripe card setup: ?card=success|cancel. Restore the canvas
  // details we stashed before the redirect (so nothing is retyped) and, on
  // success, create the canvas automatically.
  const cardReturnHandled = useRef(false);
  useEffect(() => {
    if (cardReturnHandled.current) return;
    cardReturnHandled.current = true;
    const params = new URLSearchParams(window.location.search);
    const card = params.get("card");
    const pendingRaw = window.localStorage.getItem("og.pendingCanvas");
    if (card) {
      params.delete("card");
      const qs = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
      );
    }
    if (!pendingRaw) {
      // Card added but no canvas was pending — still confirm it with Stripe so
      // it's recognised immediately (no re-login needed).
      if (card === "success") {
        trackEvent("card_added");
        void confirmCard().then(() => refreshMe()).catch(() => undefined);
      }
      return;
    }
    window.localStorage.removeItem("og.pendingCanvas");
    if (card === "success") trackEvent("card_added");
    let d: {
      workflowName?: string; namedOwner?: string; industry?: string;
      deploymentStage?: string; complexity?: string;
    };
    try {
      d = JSON.parse(pendingRaw);
    } catch {
      return;
    }
    // Restore the fields either way so the user never retypes.
    setWorkflowName(d.workflowName ?? "");
    setNamedOwner(d.namedOwner ?? "");
    setIndustry(d.industry ?? "");
    setDeploymentStage((d.deploymentStage ?? "") as DeploymentStage | "");
    setComplexity((d.complexity ?? "") as Complexity | "");
    if (card === "success") void createFromDraft(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManageSubscription = async () => {
    setManaging(true);
    try {
      window.location.href = await createPortal();
    } catch (e) {
      toast.error((e as Error).message);
      setManaging(false);
    }
  };

  const isFormValid =
    !!workflowName.trim() &&
    !!namedOwner.trim() &&
    !!industry.trim() &&
    !!deploymentStage &&
    !!complexity;

  const handleSeed = async () => {
    setSeeding(true);
    setError(null);
    try {
      await seedCanvas();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async (s: CanvasSummary) => {
    const ok = await confirm({
      title: "Delete this canvas?",
      message: `"${s.workflowName}" and all its data will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Keep",
      danger: true,
    });
    if (!ok) return;
    setDeletingId(s.id);
    try {
      await deleteCanvas(s.id);
      await refresh();
      toast.success(`Deleted "${s.workflowName}".`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreate = async () => {
    if (!isFormValid) return;
    setBusy(true);
    setError(null);
    setCardRequired(false);
    try {
      const trimmedWorkflowName = workflowName.trim();
      const { id } = await createCanvas({
        workflowName: trimmedWorkflowName,
        namedOwner: namedOwner.trim(),
        industry: industry.trim(),
        deploymentStage,
        complexity,
      });
      trackEvent("canvas_created", { source: "home" });
      if (onOpenWithUpload) {
        // Document upload is available on the free canvas too (Pascal review #3).
        onOpenWithUpload(id, trimmedWorkflowName);
      } else {
        onOpen(id);
      }
    } catch (e) {
      // Open-signup guardrail: card needed before the first canvas.
      if ((e as { code?: string }).code === "CARD_REQUIRED") {
        setCardRequired(true);
      } else {
        setError((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  // Redirect to Stripe setup checkout to validate + save a card (no charge).
  // On return, the webhook has set cardOnFile and the create will succeed.
  const goToCardSetup = async () => {
    setCardBusy(true);
    setError(null);
    // Stash the in-progress canvas details so the Stripe redirect doesn't lose
    // them — restored (and auto-created) on return. See the ?card effect below.
    window.localStorage.setItem(
      "og.pendingCanvas",
      JSON.stringify({ workflowName, namedOwner, industry, deploymentStage, complexity }),
    );
    try {
      const url = await createSetupCheckout();
      if (url) {
        window.location.href = url;
      } else {
        // Already on file (webhook already ran) — just retry the create.
        setCardRequired(false);
        await handleCreate();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCardBusy(false);
    }
  };

  return (
    <div className="home">
      <header className="home__header">
        <div className="home__brand">
          <span className="home__brand-mark" aria-hidden>◌</span>
          <span className="home__brand-name">Orchestration Coach</span>
        </div>
        <div className="home__header-right">
          <TierBadge />
          {isFree && (
            <button
              className="btn btn--primary"
              onClick={() => setUpgradePrompt("individual-plan")}
              title="Unlock unlimited canvases, the coach, reviews, and more"
            >
              Upgrade to Pro
            </button>
          )}
          {/* Only show for real Stripe subscribers. Testers upgraded to Pro
              directly in the backend have no stripeCustomerId, so the Customer
              Portal would error — hide the button for them. */}
          {!isFree && me?.stripeCustomerId && (
            <button
              className="btn btn--text"
              disabled={managing}
              onClick={() => void handleManageSubscription()}
              title="Cancel, renew, update payment, or view invoices"
            >
              {managing ? "Opening…" : "Manage subscription"}
            </button>
          )}
          <button className="btn btn--text" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      <section className="home__hero">
        <p className="home__eyebrow">Begin a new Canvas</p>
        <h1 className="home__display">
          The AI system that builds your<br />
          Orchestration Design Canvas with you.
        </h1>
        <p className="home__lede">
          Upload your documents, process maps, or deployment plans — and work through
          the six layers of the Canvas, one precise question at a time.
        </p>

        {isFree && (
          <div className="home__free-cap">
            <div className="home__free-cap-eyebrow">You're on Free</div>
            <p>
              Free includes one canvas. Upgrade to <strong>Pro</strong> for unlimited canvases,
              the coach, quarterly reviews, document upload, and on-demand reports —{" "}
              <button className="btn btn--text" onClick={() => setUpgradePrompt("individual-plan")}>upgrade to Pro</button> ($149/mo).
            </p>
          </div>
        )}

        <div className="home__form">
          <div className="field">
            <label className="field__label" htmlFor="wf">Workflow name</label>
            <input
              id="wf"
              className="field__input"
              placeholder="e.g. Pascal Bornet AI Newsletter System"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              disabled={freeAtCap}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="no">Named owner</label>
            <input
              id="no"
              className="field__input"
              placeholder="e.g. Nicolas Cravino — Technical & Safety Owner"
              value={namedOwner}
              onChange={(e) => setNamedOwner(e.target.value)}
              disabled={freeAtCap}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="ind">Industry</label>
            <input
              id="ind"
              className="field__input"
              placeholder="e.g. financial services, healthcare, e-commerce"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={freeAtCap}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="ds">Deployment stage</label>
            <select
              id="ds"
              className="field__input"
              value={deploymentStage}
              onChange={(e) => setDeploymentStage(e.target.value as DeploymentStage | "")}
              disabled={freeAtCap}
            >
              <option value="" disabled>Select stage…</option>
              <option value="new_design">new design — not yet in production</option>
              <option value="operating_system">operating system — live and running</option>
              <option value="post_incident">post-incident review</option>
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="cx">Complexity</label>
            <select
              id="cx"
              className="field__input"
              value={complexity}
              onChange={(e) => setComplexity(e.target.value as Complexity | "")}
              disabled={freeAtCap}
            >
              <option value="" disabled>Select complexity…</option>
              <option value="solo_operator">solo operator — one person, one agent</option>
              <option value="hybrid_team">hybrid team — multiple humans + agents</option>
              <option value="multi_agent">multi-agent system</option>
            </select>
          </div>
          <div className="home__form-actions">
            <button
              className="btn btn--primary btn--large"
              disabled={!isFormValid || busy || freeAtCap}
              onClick={() => void handleCreate()}
              title={freeAtCap ? "You've used your one free canvas — upgrade to start another" : undefined}
            >
              {busy ? "Creating…" : "Open Canvas →"}
            </button>
          </div>
        </div>
        {error && <div className="banner banner--error">{error}</div>}
        {cardRequired && (
          <div className="banner banner--info card-gate">
            <div className="card-gate__body">
              <strong>Add a card to start your canvas.</strong>
              <span>
                We validate a card to keep the free tier abuse-free — it is not
                charged.
              </span>
            </div>
            <button
              className="btn btn--primary"
              disabled={cardBusy}
              onClick={() => void goToCardSetup()}
            >
              {cardBusy ? "Opening…" : "Add card →"}
            </button>
          </div>
        )}
      </section>

      <section className="home__list">
        <header className="home__list-header">
          <h2 className="home__list-title">Recent Canvases</h2>
          <div className="home__list-meta">
            <span className="mono home__list-count">
              {sessions?.length ?? "—"} on file
            </span>
            {showSeedButton && (
              <button
                className="btn btn--ghost"
                disabled={seeding}
                onClick={() => void handleSeed()}
                title="Creates a fully-populated demo session with no LLM calls. Available until your first canvas."
              >
                {seeding ? "Seeding…" : "Seed demo session"}
              </button>
            )}
          </div>
        </header>
        {sessions === null ? (
          <p className="home__list-empty">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="home__list-empty">
            No reviews yet. Start one above to begin.
          </p>
        ) : (
          <ul className="sessions">
            {sessions.map((s) => {
              const isComplete = s.currentStage === "COMPLETE";
              const isDeleting = deletingId === s.id;
              return (
                <li key={s.id} className="sessions__row" data-session-id={s.id}>
                  <div className="sessions__main">
                    <div className="sessions__name">{s.workflowName}</div>
                    <div className="sessions__owner">{s.namedOwner}</div>
                  </div>
                  <div className="sessions__meta">
                    <span className={`stage stage--${s.currentStage.toLowerCase()}`}>
                      {s.currentStage}
                    </span>
                    <span className="mono sessions__date">
                      {new Date(s.updatedAt).toISOString().slice(0, 10)}
                    </span>
                    {s.versionCount !== undefined &&
                      s.versionCount > 1 &&
                      s.version !== undefined && (
                        <VersionPickerChip session={s} onOpenVersion={onOpenVersion} />
                      )}
                  </div>
                  <div className="sessions__actions">
                    {isComplete && s.hasArtifacts && (
                      <DownloadsPopover sessionId={s.id} />
                    )}
                    <button
                      className="btn btn--text"
                      onClick={() => onReview(s.id)}
                      disabled={isDeleting}
                      title="Open this canvas read-only — see every answer, score, and consistency check"
                    >
                      View
                    </button>
                    {!isComplete && (
                      <button
                        className="btn btn--text"
                        onClick={() => onOpen(s.id)}
                        disabled={isDeleting}
                      >
                        Resume
                      </button>
                    )}
                    {isComplete && onStartReview && (
                      <button
                        className="btn btn--text"
                        onClick={() => onStartReview(s.id)}
                        disabled={isDeleting || (!s.version && !s.reviewInProgress)}
                        title={
                          s.reviewInProgress
                            ? "Pick up the review you started earlier"
                            : !s.version
                              ? "Generate the Phase 9 outputs once before this canvas can be reviewed (click View → Go to outputs → Generate)"
                              : "Re-walk this canvas and produce an updated version"
                        }
                      >
                        {s.reviewInProgress ? "Resume review →" : "Review now →"}
                      </button>
                    )}
                    {isComplete && onOpenCoach && (
                      <button
                        className="btn btn--text"
                        onClick={() => {
                          if (isFree) {
                            setUpgradePrompt("coach");
                          } else {
                            onOpenCoach(s.id, s.workflowName, s.version ?? 0);
                          }
                        }}
                        disabled={isDeleting || (!isFree && !s.version)}
                        title={
                          isFree
                            ? "Paid feature — upgrade to Pro plan to talk to the coach"
                            : !s.version
                              ? "Generate the Phase 9 outputs once before talking to the coach (click View → Go to outputs → Generate)"
                              : "Open the coach to ask questions about this canvas"
                        }
                      >
                        Coach → {isFree && <PaidFeatureBadge />}
                      </button>
                    )}
                    {!isFree && (
                      <button
                        className="btn btn--text btn--danger"
                        onClick={() => void handleDelete(s)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? "Deleting…" : "Delete"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <UpgradeModal
        open={upgradePrompt !== null}
        feature={upgradePrompt ?? ""}
        onClose={() => setUpgradePrompt(null)}
      />
    </div>
  );
}

function DownloadsPopover({ sessionId }: { sessionId: string }) {
  const [open, setOpen] = useState(false);
  const [bundle, setBundle] = useState<ArtifactBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<VariantKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent) => {
      const node = wrapRef.current;
      if (node && !node.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const ensureLoaded = async () => {
    if (bundle || loading) return;
    setLoading(true);
    setError(null);
    try {
      setBundle(await getArtifacts(sessionId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleVariant = async (v: VariantKind) => {
    setBusy(v);
    setError(null);
    try {
      const signed = await generateReportVariant(sessionId, v);
      triggerDownload(signed.url, signed.filename);
      setBundle((prev) =>
        prev
          ? { ...prev, variants: { ...(prev.variants ?? {}), [v]: signed } }
          : prev
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="sessions__downloads" ref={wrapRef}>
      <button
        className="btn btn--text"
        onClick={() => {
          setOpen((o) => !o);
          void ensureLoaded();
        }}
      >
        ↓ Downloads
      </button>
      {open && (
        <div className="sessions__downloads-pop">
          {loading && <div className="sessions__downloads-sub">Loading…</div>}
          {bundle && (
            <>
              <a
                href={bundle.baselineRecord.url}
                download={bundle.baselineRecord.filename}
              >
                Baseline record
              </a>
              <a
                href={bundle.canvasJson.url}
                download={bundle.canvasJson.filename}
              >
                Canvas JSON
              </a>
              <a
                href={bundle.governanceReport.url}
                download={bundle.governanceReport.filename}
              >
                Governance report
              </a>
              <hr />
              <div className="sessions__downloads-sub">On-demand variants</div>
              {VARIANT_KINDS.map((v) => (
                <button
                  key={v}
                  disabled={busy !== null}
                  onClick={() => void handleVariant(v)}
                >
                  {busy === v ? "Generating…" : VARIANT_LABELS[v]}
                </button>
              ))}
              {bundle.bespoke && bundle.bespoke.length > 0 && (
                <>
                  <hr />
                  <div className="sessions__downloads-sub">Custom reports</div>
                  {bundle.bespoke.map((b) => (
                    <a
                      key={b.id}
                      className="sessions__downloads-bespoke"
                      href={b.url}
                      download={b.filename}
                      title={b.prompt}
                    >
                      {b.prompt}
                    </a>
                  ))}
                </>
              )}
            </>
          )}
          {error && <div className="banner banner--error">{error}</div>}
        </div>
      )}
    </div>
  );
}

// Triggers a file download from a cross-origin URL without opening a tab.
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function VersionPickerChip({
  session,
  onOpenVersion,
}: {
  session: CanvasSummary;
  onOpenVersion?: (p: {
    id: string;
    workflowName: string;
    version: number;
    createdAt: number;
    grade: string;
    composite: number;
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent) => {
      const node = wrapRef.current;
      if (node && !node.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const ensureLoaded = async () => {
    if (versions) return;
    try {
      const { versions: vs } = await listVersions(session.id);
      setVersions(vs);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="sessions__versionpicker" ref={wrapRef}>
      <button
        className="chip chip--neutral sessions__versionpicker-btn"
        onClick={() => {
          setOpen((o) => !o);
          void ensureLoaded();
        }}
        title="Pick a version of this canvas"
      >
        · v{session.version} of {session.versionCount}
      </button>
      {open && (
        <div className="sessions__versionpicker-pop">
          {error && <div className="banner banner--error">{error}</div>}
          {!versions && !error && <div>Loading…</div>}
          {versions &&
            [...versions].reverse().map((v) => (
              <button
                key={v.version}
                className="sessions__versionpicker-row"
                onClick={() => {
                  onOpenVersion?.({
                    id: session.id,
                    workflowName: session.workflowName,
                    version: v.version,
                    createdAt: v.createdAt,
                    grade: v.grade,
                    composite: v.composite,
                  });
                  setOpen(false);
                }}
              >
                <span className="mono">v{v.version}</span>
                <span>{new Date(v.createdAt).toISOString().slice(0, 10)}</span>
                <span>
                  Grade {v.grade} ({v.composite.toFixed(2)})
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
